import { formatUnits } from 'viem';

import { formatExactDecimal } from './exact-decimal';

/** Digits kept by `formatRoundedUnits`. */
const ROUNDED_DIGITS = 6;

/**
 * A token amount in base units, rounded to read: half up to six decimal
 * places, or to six significant digits for an amount under 1, so a small
 * fee never reads as zero. Grouped like `formatExactDecimal`. A negative
 * amount is rounded by its size and carries a true minus sign ("−1.234568"),
 * so it rounds the same way. For the figure digit for digit, use
 * `formatExactUnits`.
 *
 * `round: 'down'` for a quote of what someone receives (an ERC-4626
 * preview is a "no more than" figure): it never reads higher than it is.
 */
export function formatRoundedUnits(
  value: bigint,
  decimals: number,
  { round = 'half-up' }: { round?: 'half-up' | 'down' } = {}
): string {
  if (value < 0n) {
    return `−${formatRoundedUnits(-value, decimals, { round })}`;
  }
  const exact = formatUnits(value, decimals);
  const [whole = '0', fraction = ''] = exact.split('.');
  const leadingZeros =
    whole === '0' ? (/^0*/.exec(fraction)?.[0].length ?? 0) : 0;
  const keep = leadingZeros + ROUNDED_DIGITS;
  if (fraction.length <= keep) return formatExactDecimal(exact);
  const unit = 10n ** BigInt(decimals - keep);
  const half = round === 'down' ? 0n : unit / 2n;
  return formatExactDecimal(formatUnits((value + half) / unit, keep));
}
